import React from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';

import Breakpoints from '../../design-utils/breakpoints';
import MinusIcon from '../Icons/MinusIcon';
import PlusIcon from '../Icons/PlusIcon';
import { H4 } from '../Typography/';

const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 200px;
  @media screen and (max-width: ${Breakpoints.md}) {
    width: 240px;
    height: 400px;
  }
  height: auto;
  text-align: center;
`;

const ProductImage = styled.img`
  width: 100%;
  border-radius: 8px;
  object-fit: contain;
  max-height: 210px;
`;

export const CardSubSubTitle = styled(H4)`
  margin: 5px 0;
`;

const Empty = styled.div<{
  height: number;
  backgroundColor?: string;
  borderRadius?: number;
}>`
  height: ${({ height }) => `${height}px`};
  border-radius: ${({ borderRadius }) => `${borderRadius}px`};
  background-color: ${({ backgroundColor }) => backgroundColor};
  &:empty {
    display: block;
  }
`;

const CustomMinusIcon = styled(MinusIcon)`
  width: 16px;
  height: 16px;
`;

const CustomPlusIcon = styled(PlusIcon)`
  width: 16px;
  height: 16px;
`;

interface QuantityButtonContainerProps {
  available?: boolean;
}

const QuantityButtonContainer = styled.div<{ available?: boolean }>`
  display: flex;
  justify-content: space-between;
  border: 1px solid #c4cdd5;
  padding: 12px;
  border-radius: 22px;
  font-weight: 400;
  font-size: 14px;
  background: ${({ available }) => (available ? 'transparent' : '#f6f6f6')};
`;

const QuantityButton = styled.button`
  background: none;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: initial;
  height: initial;

  &:hover {
    background-color: initial;
  }

  &:disabled {
    visibility: hidden;
  }
`;

const Quantity = styled.span`
  &:disabled {
    opacity: 0.3;
  }
`;

export const AddQuantityButton = ({
  onDecrease,
  onIncrease,
  quantity,
  available,
  isIncreaseDisabled = false,
  isDecreaseDisabled = false,
  ...props
}: {
  onDecrease: () => void;
  onIncrease: () => void;
  quantity: number;
  available: boolean;
  isIncreaseDisabled?: boolean;
  isDecreaseDisabled?: boolean;
  [key: string]: unknown;
}): JSX.Element => {
  const { t } = useTranslation();
  return (
    <QuantityButtonContainer
      className={`${
        available
          ? 'bundle-product-card__quantity-button-container'
          : 'bundle-product-card__quantity-button-container bundle-product-card__quantity-button-container--unavailable'
      }`}
      available={available}
      {...props}
    >
      <QuantityButton
        className="bundle-product-card__quantity-button-container--button"
        data-testid="quantity-button"
        disabled={!available || isDecreaseDisabled}
        onClick={onDecrease}
        aria-label={t('aria_labels.decrease_quantity')}
      >
        <CustomMinusIcon />
      </QuantityButton>
      <Quantity>{available ? quantity : t('product_swap.sold_out')}</Quantity>
      <QuantityButton
        className="bundle-product-card__quantity-button-container--button"
        data-testid="quantity-button"
        disabled={!available || isIncreaseDisabled}
        onClick={onIncrease}
        aria-label={t('aria_labels.increase_quantity')}
      >
        <CustomPlusIcon />
      </QuantityButton>
    </QuantityButtonContainer>
  );
};

export const CardImage = ({
  image,
  alt,
}: {
  image?: string;
  alt: string;
}): JSX.Element =>
  image ? (
    <ProductImage
      className="bundle-product-card__image"
      src={image}
      alt={alt}
    />
  ) : (
    <Empty
      className="bundle-product-card--empty"
      height={140}
      backgroundColor="#fafafa"
      borderRadius={8}
    />
  );

export interface ProductCardProps {
  children?: React.ReactElement | React.ReactElement[];
  onCardClick?: (id: string) => void;
  id?: string;
}

const ProductCard: React.FC<ProductCardProps> = ({
  id,
  children,
  onCardClick,
}) => {
  if (!id) return null;
  return (
    <CardContainer
      className={`bundle-product-card p_${id}`}
      data-testid="product-card"
      data-pid={id}
    >
      <div
        data-testid="product-card-clickable-area"
        style={{ cursor: onCardClick ? 'pointer' : 'unset' }}
        onClick={onCardClick ? () => onCardClick(id) : undefined}
      >
        {children}
      </div>
    </CardContainer>
  );
};

export default ProductCard;
